import { skipToken } from '@apollo/client/react'
import { Checkbox, Divider, FormControlLabel } from '@mui/material'
import { produce } from 'immer'
import { pick } from 'lodash-es'
import type { FC } from 'react'
import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import { useAsyncFn } from 'react-use'
import { match, P } from 'ts-pattern'
import IcBaselineFlag from '~icons/ic/baseline-flag'
import IcRoundCheck from '~icons/ic/round-check'
import { CircularSpinner } from 'src/components/GenerationTask/Common/CircularSpinner'
import { dialog } from 'src/components/Modal/DynamicModal'
import { UIButton } from 'src/components/ui/Button'
import {
  UIDialog,
  UIDialogContent,
  UIDialogFooter,
  UIDialogHeader,
} from 'src/components/ui/Modal'
import UISwitch from 'src/components/ui/Switch'
import {
  executeOpModerationNSFW,
  executeOpRefreshMediaSafetyScore,
  executeOpRefreshTextSafetyScore,
} from 'src/services/apollo/adminOp'
import {
  executeDeleteArtwork,
  executeUpsertArtwork,
  useGetArtwork,
} from 'src/services/apollo/artwork'
import type { ModerationFlag } from 'src/services/apollo/generated/graphql'
import { RefType } from 'src/services/apollo/generated/graphql'
import {
  executeDeleteGenerationModel,
  useGetGenerationModel,
} from 'src/services/apollo/generation-model'

type ModerationFlags = keyof Pick<
  ModerationFlag,
  'isMinors' | 'isRealistic' | 'isSensitive'
>
type ModerationFlagValues = Pick<ModerationFlag, ModerationFlags>

type ModerationScoreValues = {
  textSafetyScore?: number
  mediaSafetyScore?: number
}

type ModerationEditDialogProps = {
  refIds: string[]
  refType: typeof RefType.Artwork | typeof RefType.Model
  onComplete?: () => void
  onRefresh?: () => void
  values: ModerationFlagValues
  textSafetyScore?: number
  mediaSafetyScore?: number
}

const getClassNameForSafetyScore = (value: number | undefined) => {
  return match(value)
    .with(P.nullish, () => '')
    .when(
      v => v < 2,
      () => 'text-red-600 dark:text-red-500',
    )
    .when(
      v => v < 4,
      () => 'text-orange-600 dark:text-orange-500',
    )
    .when(
      v => v < 6,
      () => 'text-amber-600 dark:text-amber-500',
    )
    .when(
      v => v < 8,
      () => 'text-yellow-600 dark:text-yellow-500',
    )
    .otherwise(() => 'text-lime-600 dark:text-lime-500')
}

function SafetyScoreDisplay(props: { score?: number }) {
  return (
    <div className={getClassNameForSafetyScore(props.score)}>
      {props.score === undefined ? '?' : props.score}
    </div>
  )
}

const safetyScoreOptions = [
  {
    score: 1,
    label: '1(cp)',
    className: 'bg-red-500 hover:bg-red-400',
  },
  {
    score: 3,
    label: '3(nsfw)',
    className: 'bg-orange-500 hover:bg-orange-400',
  },
  {
    score: 6,
    label: '6(sexy)',
    className: 'bg-yellow-500 hover:bg-yellow-400',
  },
  {
    score: 8,
    label: '8(safe)',
    className: 'bg-lime-500 hover:bg-lime-400',
  },
]

function SafetyScoreEditor(props: { setScore?: (score: number) => void }) {
  return (
    <div className="flex gap-1">
      {safetyScoreOptions.map(({ score, label, className }) => (
        <UIButton
          size="small"
          variant="filled"
          className={className}
          onPress={() => {
            props.setScore?.(score)
          }}
        >
          {label}
        </UIButton>
      ))}
    </div>
  )
}

export const ModerationEditDialog: FC<ModerationEditDialogProps> = ({
  refIds,
  refType,
  onComplete,
  onRefresh,
  values,
  textSafetyScore,
  mediaSafetyScore,
}) => {
  const [modValues, setModValues] = useState({
    ...defaultFlags,
    ...values,
  } as Required<ModerationFlagValues>)
  const [modScores, setModScores] = useState({
    textSafetyScore,
    mediaSafetyScore,
  } as ModerationScoreValues)

  const [textSafetyScoreRefreshState, refreshTextSafetyScore] =
    useAsyncFn(async () => {
      if (refType !== RefType.Artwork && refType !== RefType.Model) {
        return toast.error(`Unexpected moderation type ${refType}`)
      }
      const modFn = (refId: string) =>
        executeOpRefreshTextSafetyScore({
          input:
            refType === RefType.Artwork
              ? { artworkId: refId }
              : { modelId: refId },
        })
      try {
        for (const refId of refIds) {
          const result = await modFn(refId)
          const textSafetyScore =
            result.data?.opRefreshTextSafetyScore?.textSafetyScore
          if (textSafetyScore) {
            setModScores(scores => ({ ...scores, textSafetyScore }))
            toast.success(
              `Text safety score refreshed for: [${refId}], now is ${textSafetyScore}`,
            )
            onRefresh?.()
          } else {
            toast.error(`Failed to refresh text safety score for: [${refId}]`)
          }
        }
      } catch (e) {
        const err = e as Error
        toast.error(err.message)
      }
    }, [refIds, refType])

  const [mediaSafetyScoreRefreshState, refreshMediaSafetyScore] =
    useAsyncFn(async () => {
      if (refType !== RefType.Artwork) {
        return toast.error(
          `Media safety score refresh is only available for artworks`,
        )
      }
      const modFn = (refId: string) =>
        executeOpRefreshMediaSafetyScore({
          input: { artworkId: refId },
        })
      try {
        for (const refId of refIds) {
          const result = await modFn(refId)
          const mediaSafetyScore =
            result.data?.opRefreshMediaSafetyScore?.mediaSafetyScore
          if (mediaSafetyScore) {
            setModScores(scores => ({ ...scores, mediaSafetyScore }))
            toast.success(
              `Media safety score refreshed for: [${refId}], now is ${mediaSafetyScore}`,
            )
            onRefresh?.()
          } else {
            toast.error(`Failed to refresh media safety score for: [${refId}]`)
          }
        }
      } catch (e) {
        const err = e as Error
        toast.error(err.message)
      }
    }, [refIds, refType])

  const [submitting, setSubmitting] = useState(false)

  const submit = async () => {
    setSubmitting(true)
    if (refType !== RefType.Artwork && refType !== RefType.Model) {
      return toast.error(`Unexpected moderation type ${refType}`)
    }
    const modFn = (refId: string) =>
      executeOpModerationNSFW({
        input: {
          ...(refType === RefType.Artwork
            ? { artworkId: refId }
            : { modelId: refId }),

          ...pick(modValues, ['isMinors', 'isRealistic', 'isSensitive']),
          ...modScores,
          // Automatically resolve related reports
          resolveReport: true,
        },
      })

    try {
      for (const refId of refIds) {
        // If minors is selected, show dialog for admin to confirm deletion
        if (modValues.isMinors) {
          try {
            const toProceed = await UIDialog.confirm({
              heading: `You've marked this entity as MINORS`,
              content:
                'Proceeding this action will DELETE this entity. Confirm?',
            })
            if (!toProceed) return

            await modFn(refId)
            toast.success(`Moderation successfully updated: [${refId}]`)
            if (refType === RefType.Artwork) {
              await executeDeleteArtwork({ id: refId })
            } else {
              await executeDeleteGenerationModel({ id: refId })
            }
            toast.success('Entity successfully deleted: [${refId}]')
          } catch (e: any) {
            toast.error(e?.message)
          }
        } else {
          // Minors not selected, proceed with normal moderation
          await modFn(refId)
          toast.success(`Moderation successfully updated: [${refId}]`)
        }
      }
      onComplete?.()
    } catch (e) {
      const err = e as Error
      toast.error(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleFlagChange = useCallback(
    (flag: ModerationFlags, checked: boolean) => {
      return setModValues(
        produce(draft => {
          draft[flag] = checked
        }),
      )
    },
    [],
  )

  return (
    <>
      <UIDialogHeader
        heading={
          <span className="inline-flex items-center gap-2">
            <IcBaselineFlag className="w-6" color="inherit" />
            <span>
              {refType === RefType.Artwork
                ? 'Adjust Artwork Moderation'
                : 'Adjust Model Moderation'}
            </span>
          </span>
        }
      />
      <UIDialogContent>
        <div className="flex flex-col">
          <h2 className="text-md">Current Safety Score</h2>
          <div className="grid grid-cols-[60px_36px_80px] grid-rows-2 gap-1 items-center my-2">
            <div>Media:</div>
            <SafetyScoreDisplay score={modScores.mediaSafetyScore} />
            {refType === RefType.Artwork && (
              <UIButton
                size="small"
                variant="filled"
                color="normal"
                onPress={() => {
                  refreshMediaSafetyScore()
                }}
                busy={mediaSafetyScoreRefreshState.loading}
              >
                Refresh
              </UIButton>
            )}
            <div>Text:</div>
            <SafetyScoreDisplay score={modScores.textSafetyScore} />
            <UIButton
              size="small"
              variant="filled"
              color="normal"
              onPress={() => {
                refreshTextSafetyScore()
              }}
              busy={textSafetyScoreRefreshState.loading}
            >
              Refresh
            </UIButton>
          </div>
          <h2 className="text-md">Adjust Safety Score</h2>
          <div className="grid grid-cols-[60px_auto] grid-rows-2 gap-1 items-center my-2">
            <div>Media:</div>
            <SafetyScoreEditor
              setScore={score =>
                setModScores(scores => ({ ...scores, mediaSafetyScore: score }))
              }
            />
            <div>Text:</div>
            <SafetyScoreEditor
              setScore={score =>
                setModScores(scores => ({ ...scores, textSafetyScore: score }))
              }
            />
          </div>
          <Divider className="py-1" />
          <FormControlLabel
            checked={modValues.isSensitive}
            control={
              <Checkbox
                color="error"
                onChange={e =>
                  handleFlagChange('isSensitive', e.target.checked)
                }
              />
            }
            label={
              <div className="rounded-md bg-red-700 text-white px-2">
                Sensitive
              </div>
            }
          />
          <FormControlLabel
            checked={modValues.isMinors}
            control={
              <Checkbox
                color="error"
                onChange={e => handleFlagChange('isMinors', e.target.checked)}
              />
            }
            label={
              <div className="rounded-md bg-red-700 text-white px-2">
                Minors
              </div>
            }
          />
          <FormControlLabel
            checked={modValues.isRealistic}
            control={
              <Checkbox
                color="warning"
                onChange={e =>
                  handleFlagChange('isRealistic', e.target.checked)
                }
              />
            }
            label={
              <div className="rounded-md bg-yellow-700 text-white px-2">
                Realistic
              </div>
            }
          />
        </div>
      </UIDialogContent>
      <UIDialogFooter>
        <UIButton
          size="large"
          variant="filled"
          color="normal"
          onPress={() => {
            submit()
          }}
          busy={submitting}
          icon={<IcRoundCheck />}
        >
          <span>Submit</span>
        </UIButton>
      </UIDialogFooter>
    </>
  )
}

const defaultFlags: ModerationFlagValues = {
  isMinors: false,
  isRealistic: false,
  isSensitive: false,
}

function ModerationEditDialogFetchWrapper(
  props: Omit<ModerationEditDialogProps, 'values'> & {
    values?: ModerationFlagValues
    shouldFetch?: boolean
  },
) {
  const { refType, refIds } = props
  const { data: modelData, loading: fetchingModel } = useGetGenerationModel(
    refType === RefType.Model ? refIds[0] : skipToken,
  )
  const { data: artworkData, loading: fetchingArtwork } = useGetArtwork(
    refType === RefType.Artwork ? refIds[0] : skipToken,
  )

  if (fetchingModel || fetchingArtwork) return <CircularSpinner />
  const fetchedEntity = refType === RefType.Artwork ? artworkData : modelData

  const values = props.values ?? fetchedEntity?.flag ?? defaultFlags

  const { textSafetyScore, mediaSafetyScore } = fetchedEntity?.extra ?? {}

  return (
    <ModerationEditDialog
      {...props}
      values={values}
      textSafetyScore={textSafetyScore}
      mediaSafetyScore={mediaSafetyScore}
    />
  )
}

export const dialogModerationEdit = async ({
  refType,
  refIds,
  values,
  shouldFetch,
  mutate,
}: {
  refType: typeof RefType.Artwork | typeof RefType.Model
  refIds: string[]
  mutate?: () => void
  values?: ModerationFlagValues
  shouldFetch?: boolean
}) => {
  return UIDialog.create({
    dismissable: true,
    className: 'max-w-screen-md',
    children: ({ close }) =>
      refIds.length > 1 ? (
        <ModerationEditDialog
          refIds={refIds}
          refType={refType}
          values={values ?? defaultFlags}
          onRefresh={() => {
            mutate?.()
          }}
          onComplete={() => {
            mutate?.()
            close()
          }}
        />
      ) : (
        <ModerationEditDialogFetchWrapper
          values={values}
          shouldFetch={shouldFetch}
          refType={refType}
          refIds={refIds}
          onComplete={() => {
            mutate?.()
            close()
          }}
        />
      ),
  })
}

/**
 * Archive Flag Edit Dialog
 * @param param0
 */
export const dialogModerationArchiveEdit = ({
  refType,
  refId,
  values,
  shouldFetch,
  mutate,
}: {
  refType: typeof RefType.Artwork
  refId: string
  mutate?: () => void
} & (
  | { values: boolean; shouldFetch?: never }
  | {
      values?: never
      shouldFetch: true
    }
)) => {
  const dialogInstance = dialog({
    children: (
      <ModerationArchiveDialogFetchWrapper
        values={values}
        shouldFetch={shouldFetch}
        refType={refType}
        refId={refId}
        onComplete={() => {
          mutate?.()
          dialogInstance.current?.close()
        }}
      />
    ),
    fullWidth: true,
    maxWidth: 'sm',
    classes: {
      paper: 'rounded-xl',
    },
  })
}

function ModerationArchiveDialogFetchWrapper(
  props: Omit<ModerationArchiveDialogProps, 'values'> & {
    values?: boolean
    shouldFetch?: boolean
  },
) {
  const { refType, refId } = props
  const { data: artworkData, loading: fetchingArtwork } = useGetArtwork(
    refType === RefType.Artwork ? refId : skipToken,
  )

  if (fetchingArtwork) return <CircularSpinner />

  const values = props.values ?? artworkData?.extra.archived ?? false

  return <ModerationArchiveDialog {...props} values={values} />
}
type ModerationArchiveDialogProps = {
  refId: string
  refType: typeof RefType.Artwork
  onComplete?: () => void
  values: boolean
}

export const ModerationArchiveDialog: FC<ModerationArchiveDialogProps> = ({
  refId,
  refType,
  onComplete,
  values,
}) => {
  const [isArchived, setIsArchived] = useState(values)
  const [submitting, setSubmitting] = useState(false)

  const submit = async () => {
    setSubmitting(true)

    try {
      if (refType !== RefType.Artwork) {
        return toast.error('Wrong moderation type')
      }
      await executeUpsertArtwork({
        id: refId,
        input: {
          extra: {
            archived: isArchived,
          },
        },
      })
      toast.success(`Archive status successfully updated`)
      onComplete?.()
    } catch (e) {
      const err = e as Error
      toast.error(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <UIDialogHeader
        heading={
          <span className="inline-flex items-center gap-2">
            <IcBaselineFlag className="w-6" color="inherit" />
            <span>
              {refType === RefType.Artwork
                ? 'Adjust Artwork Moderations'
                : 'Adjust Model Moderations'}
            </span>
          </span>
        }
      />
      <UIDialogContent>
        <div className="flex flex-col mt-8">
          <div className="flex gap-2">
            <UISwitch
              checked={isArchived}
              onCheckedChange={checked => {
                setIsArchived(checked)
              }}
            />
            Archive
          </div>
        </div>
      </UIDialogContent>
      <UIDialogFooter>
        <UIButton
          size="large"
          variant="filled"
          color="normal"
          onPress={() => {
            submit()
          }}
          busy={submitting}
          icon={<IcRoundCheck />}
        >
          <span>Submit</span>
        </UIButton>
      </UIDialogFooter>
    </>
  )
}
